import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const AdvertsPage = () => {
  const [adverts, setAdverts] = useState([]);
  const [appliedAdverts, setAppliedAdverts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAdvert, setSelectedAdvert] = useState(null);
  const [view, setView] = useState('available'); // 'available' or 'applied'
  const modalRef = useRef(null);
  const modalInstanceRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isCompanyAuthenticated = localStorage.getItem('companyAuthenticated') === 'true';
  const companyId = localStorage.getItem('companyId');
  const applyAdvertId = location.state?.applyAdvertId;

  useEffect(() => {
    const fetchAdverts = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch('https://dropquote.co.za/backend/get_adverts.php', {
          headers: { 'Content-Type': 'application/json' },
        });
        const result = await response.json();
        if (result.success) {
          setAdverts(result.data);
        } else {
          setError(result.message || 'Failed to fetch adverts');
        }
      } catch (error) {
        setError('Error fetching adverts: ' + error.message);
      } finally {
        setLoading(false);
      }
    };

    const fetchAppliedAdverts = async () => {
      if (!companyId) {
        setError('No company ID found. Please log in again.');
        setLoading(false);
        navigate('/company-auth');
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `https://dropquote.co.za/backend/get_applied_adverts.php?companyId=${companyId}`,
          {
            headers: { 'Content-Type': 'application/json' },
          }
        );
        const result = await response.json();
        if (result.success) {
          setAppliedAdverts(result.data);
        } else {
          setError(result.message || 'Failed to fetch applied adverts');
        }
      } catch (error) {
        setError('Error fetching applied adverts: ' + error.message);
      } finally {
        setLoading(false);
      }
    };

    if (view === 'available') {
      fetchAdverts();
    } else {
      fetchAppliedAdverts();
    }

    if (isCompanyAuthenticated && applyAdvertId) {
      navigate('/apply', { state: { advertId: applyAdvertId } });
    }
  }, [applyAdvertId, isCompanyAuthenticated, companyId, navigate, view]);

  useEffect(() => {
    if (window.bootstrap && modalRef.current && !modalInstanceRef.current) {
      modalInstanceRef.current = new window.bootstrap.Modal(modalRef.current);
    }
    return () => {
      if (modalInstanceRef.current) {
        modalInstanceRef.current.dispose();
        modalInstanceRef.current = null;
      }
    };
  }, []);

  const handleApply = (advertId) => {
    console.log('Applying for advertId:', advertId);
    if (!advertId) {
      setError('No advert ID provided for apply action');
      return;
    }
    try {
      const parsedCompanyId = parseInt(companyId, 10);
      if (!isCompanyAuthenticated || !companyId || isNaN(parsedCompanyId) || parsedCompanyId <= 0) {
        console.log('Redirecting to company-auth due to invalid auth:', {
          isCompanyAuthenticated,
          companyId,
          parsedCompanyId,
        });
        navigate('/company-auth', { state: { advertId } });
      } else {
        navigate('/apply', { state: { advertId } });
      }
    } catch (navError) {
      console.error('Navigation error:', navError);
      setError('Failed to navigate to apply page: ' + navError.message);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('companyAuthenticated');
    localStorage.removeItem('companyId');
    navigate('/');
  };

  const viewDetails = (advert) => {
    setSelectedAdvert(advert);
    if (!modalInstanceRef.current && window.bootstrap && modalRef.current) {
      modalInstanceRef.current = new window.bootstrap.Modal(modalRef.current);
    }
    if (modalInstanceRef.current) {
      modalInstanceRef.current.show();
    }
  };

  return (
    <div className="container-fluid mt-3">
      <div className="row">
        {/* Sidebar */}
        <div className="col-md-2 bg-dark border-end p-3">
          <h5 className="fw-bold text-white mb-4">Navigation</h5>
          <div className="d-flex flex-column gap-2">
            <button
              className={`btn btn-outline-light rounded-pill ${view === 'available' ? 'active' : ''}`}
              onClick={() => setView('available')}
            >
              <i className="bi bi-list-ul me-2"></i>Available Adverts
            </button>
            <button
              className={`btn btn-outline-light rounded-pill ${view === 'applied' ? 'active' : ''}`}
              onClick={() => setView('applied')}
            >
              <i className="bi bi-check-square me-2"></i>Applied Adverts
            </button>
            {isCompanyAuthenticated && (
              <button
                className="btn btn-outline-light rounded-pill"
                onClick={handleLogout}
              >
                <i className="bi bi-box-arrow-right me-2"></i>Logout
              </button>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="col-md-10 p-4">
          <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
            <h1 className="display-5 fw-bold text-dark">
              {view === 'available' ? 'Available Logistics Adverts' : 'Your Applied Adverts'}
            </h1>
            <div className="d-flex align-items-center gap-2">
              {isCompanyAuthenticated && (
                <button className="btn btn-outline-dark rounded-pill" onClick={handleLogout}>
                  <i className="bi bi-box-arrow-right me-2"></i>Logout
                </button>
              )}
              <button
                className="btn btn-outline-dark rounded-pill"
                onClick={() => navigate('/login')}
              >
                <i className="bi bi-person-lock me-2"></i>Admin Login
              </button>
            </div>
          </div>

          {loading && (
            <div className="text-center py-4">
              <div className="spinner-border text-danger" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2 text-muted">Fetching {view === 'available' ? 'adverts' : 'applied adverts'}...</p>
            </div>
          )}

          {error && (
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              {error}
              <button
                type="button"
                className="btn-close"
                onClick={() => setError(null)}
                aria-label="Close"
              ></button>
            </div>
          )}

          {!loading && !error && (view === 'available' ? adverts : appliedAdverts).length > 0 ? (
            <div className="list-group">
              {(view === 'available' ? adverts : appliedAdverts).map((advert, index) => (
                <div
                  key={advert.id}
                  className={`list-group-item ${index % 2 === 0 ? 'bg-light' : 'bg-white'}`}
                >
                  <div className="d-flex w-100 justify-content-between align-items-center">
                    <div>
                      <h5 className="mb-1 text-dark">Advert #{advert.id}</h5>
                      <div className="row">
                        <div className="col-md-4">
                          <p className="mb-1 advert-text"><strong>Collection Point:</strong> {advert.collection_point || 'N/A'}</p>
                          <p className="mb-1 advert-text"><strong>Collection Date:</strong> {advert.collection_date || 'N/A'}</p>
                          <p className="mb-1 advert-text"><strong>Package Type:</strong> {advert.package_type || 'N/A'}</p>
                        </div>
                        <div className="col-md-4">
                          <p className="mb-1 advert-text"><strong>Drop-off Point:</strong> {advert.drop_off_point || 'N/A'}</p>
                          <p className="mb-1 advert-text"><strong>Delivery Date:</strong> {advert.delivery_date || 'N/A'}</p>
                          <p className="mb-1 advert-text"><strong>Parcels:</strong> {advert.number_of_parcels || 'N/A'}</p>
                          <p className="mb-1 advert-text"><strong>Pallets:</strong> {advert.number_of_pallets || 'N/A'}</p>
                        </div>
                        <div className="col-md-4">
                          <p className="mb-1 advert-text"><strong>Weight:</strong> {advert.parcel_weight || 'N/A'} KG</p>
                          <p className="mb-1 advert-text"><strong>Volume:</strong> {advert.volumes_in_tubes || 'N/A'} m³</p>
                          <p className="mb-1 advert-text"><strong>Quote By:</strong> {advert.date_quote_by || 'N/A'}</p>
                          <p className="mb-1 advert-text"><strong>Created:</strong> {advert.created_at || 'N/A'}</p>
                          {view === 'applied' && (
                            <>
                              <p className="mb-1 advert-text"><strong>Quote Price:</strong> R {advert.quote_price || 'N/A'}</p>
                              <p className="mb-1 advert-text"><strong>Est. Delivery Days:</strong> {advert.estimated_delivery_days || 'N/A'}</p>
                              <p className="mb-1 advert-text"><strong>Applied At:</strong> {advert.applied_at || 'N/A'}</p>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="d-flex flex-column gap-2">
                      {view === 'available' && (
                        <button
                          className="btn btn-danger btn-sm rounded-pill px-3"
                          onClick={() => handleApply(advert.id)}
                        >
                          <i className="bi bi-check-circle me-1"></i>Apply
                        </button>
                      )}
                      <button
                        className="btn btn-outline-dark btn-sm rounded-pill px-3"
                        onClick={() => viewDetails(advert)}
                      >
                        <i className="bi bi-info-circle me-1"></i>Details
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : !loading && !error ? (
            <div className="text-center py-5">
              <i className="bi bi-inbox display-1 text-muted"></i>
              <p className="mt-3 text-muted fs-5">
                No {view === 'available' ? 'adverts' : 'applied adverts'} available at this time.
              </p>
            </div>
          ) : null}

          {/* Modal for Detailed View */}
          <div
            ref={modalRef}
            className="modal fade"
            id="advertDetailsModal"
            tabIndex="-1"
            aria-labelledby="advertDetailsModalLabel-secondly"
            aria-hidden="true"
          >
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header bg-danger text-white">
                  <h5 className="modal-title fw-bold advert-text" id="advertDetailsModalLabel-secondly">
                    Advert #{selectedAdvert?.id || 'N/A'} Details
                  </h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
                  {selectedAdvert && (
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <h6 className="fw-bold text-dark">Package Type</h6>
                        <p className="advert-text">{selectedAdvert.package_type || 'N/A'}</p>
                      </div>
                      <div className="col-md-6 mb-3">
                        <h6 className="fw-bold text-dark">Collection Point</h6>
                        <p className="advert-text">{selectedAdvert.collection_point || 'N/A'}</p>
                      </div>
                      <div className="col-md-6 mb-3">
                        <h6 className="fw-bold text-dark">Collection Date</h6>
                        <p className="advert-text">{selectedAdvert.collection_date || 'N/A'}</p>
                      </div>
                      <div className="col-md-6 mb-3">
                        <h6 className="fw-bold text-dark">Drop-off Point</h6>
                        <p className="advert-text">{selectedAdvert.drop_off_point || 'N/A'}</p>
                      </div>
                      <div className="col-md-6 mb-3">
                        <h6 className="fw-bold text-dark">Delivery Date</h6>
                        <p className="advert-text">{selectedAdvert.delivery_date || 'N/A'}</p>
                      </div>
                      <div className="col-md-6 mb-3">
                        <h6 className="fw-bold text-dark">Number of Parcels</h6>
                        <p className="advert-text">{selectedAdvert.number_of_parcels || 'N/A'}</p>
                      </div>
                      <div className="col-md-6 mb-3">
                        <h6 className="fw-bold text-dark">Number of Pallets</h6>
                        <p className="advert-text">{selectedAdvert.number_of_pallets || 'N/A'}</p>
                      </div>
                      <div className="col-md-6 mb-3">
                        <h6 className="fw-bold text-dark">Parcel Weight (KG)</h6>
                        <p className="advert-text">{selectedAdvert.parcel_weight || 'N/A'}</p>
                      </div>
                      <div className="col-md-6 mb-3">
                        <h6 className="fw-bold text-dark">Parcel Volume (m³)</h6>
                        <p className="advert-text">{selectedAdvert.volumes_in_tubes || 'N/A'}</p>
                      </div>
                      <div className="col-md-6 mb-3">
                        <h6 className="fw-bold text-dark">Quote Closing Date</h6>
                        <p className="advert-text">{selectedAdvert.date_quote_by || 'N/A'}</p>
                      </div>
                      <div className="col-md-6 mb-3">
                        <h6 className="fw-bold text-dark">Created Date</h6>
                        <p className="advert-text">{selectedAdvert.created_at || 'N/A'}</p>
                      </div>
                      {view === 'applied' && (
                        <>
                          <div className="col-md-6 mb-3">
                            <h6 className="fw-bold text-dark">Quote Price</h6>
                            <p className="advert-text">R {selectedAdvert.quote_price || 'N/A'}</p>
                          </div>
                          <div className="col-md-6 mb-3">
                            <h6 className="fw-bold text-dark">Estimated Delivery Days</h6>
                            <p className="advert-text">{selectedAdvert.estimated_delivery_days || 'N/A'}</p>
                          </div>
                          <div className="col-md-6 mb-3">
                            <h6 className="fw-bold text-dark">Notes</h6>
                            <p className="advert-text">{selectedAdvert.notes || 'N/A'}</p>
                          </div>
                          <div className="col-md-6 mb-3">
                            <h6 className="fw-bold text-dark">Invoice</h6>
                            <p className="advert-text">
                              {selectedAdvert.invoice_path ? (
                                <a
                                  href={`https://dropquote.co.za/backend/${selectedAdvert.invoice_path}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-danger"
                                >
                                  View Invoice
                                </a>
                              ) : (
                                'N/A'
                              )}
                            </p>
                          </div>
                          <div className="col-md-6 mb-3">
                            <h6 className="fw-bold text-dark">Applied At</h6>
                            <p className="advert-text">{selectedAdvert.applied_at || 'N/A'}</p>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-dark rounded-pill"
                    data-bs-dismiss="modal"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvertsPage;